import React, { useRef, useState } from 'react';
import { Stack, Typography, Button, IconButton, Box } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { Image } from 'antd';
import 'antd/dist/reset.css'; // make sure Ant Design styles are imported

interface MuiUploadProps {
  label: string;
  accept?: string;
  multiple?: boolean;
  maxSize?: number;
  maxFiles?: number;
  replaceOnUpload?: boolean;
  files: (File | string)[];
  onChange: (files: (File | string)[]) => void;
  onRemove: (file: File | string) => void;
  onRemoveAll: () => void;
}

const MuiUpload: React.FC<MuiUploadProps> = ({
  label,
  accept,
  multiple,
  maxSize,
  maxFiles,
  replaceOnUpload,
  files,
  onChange,
  onRemove,
  onRemoveAll,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const processFiles = (fileList: FileList) => {
    const validFiles = Array.from(fileList).filter(
      (file) => !maxSize || file.size <= maxSize
    );

    let updatedFiles: (File | string)[];
    if (replaceOnUpload) {
      updatedFiles = validFiles.slice(0, 1);
    } else {
      updatedFiles = [...files, ...validFiles];
      if (maxFiles && updatedFiles.length > maxFiles) {
        updatedFiles = updatedFiles.slice(0, maxFiles);
      }
    }
    onChange(updatedFiles);
  };

  const handleFiles = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) processFiles(event.target.files);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    if (event.dataTransfer.files) processFiles(event.dataTransfer.files);
  };

  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle2">{label}</Typography>

      {/* Drag & Drop Zone */}
      <Box
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        sx={{
          border: '2px dashed',
          borderColor: isDragging ? 'primary.main' : 'grey.400',
          borderRadius: 2,
          py: 6,
          px: 2,
          textAlign: 'center',
          bgcolor: '#f9f9f9',
          cursor: 'pointer',
          transition: '0.2s',
        }}
      >
        <Typography variant="subtitle1" color="text.secondary">
          Drop or select file
        </Typography>
      </Box>

      <input
        type="file"
        ref={inputRef}
        style={{ display: 'none' }}
        accept={accept}
        multiple={multiple}
        onChange={handleFiles}
      />

      {/* Image Preview using Ant Design */}
      {accept?.includes('image') && files.length > 0 && (
        <Image.PreviewGroup>
          <Stack direction="row" spacing={2} flexWrap="wrap">
            {files.map((file, index) => {
              const isFileObj = file instanceof File;
              const src = isFileObj ? URL.createObjectURL(file) : file;

              return (
                <Box key={index} sx={{ position: 'relative' }}>
                  <Image
                    src={src}
                    width={120}
                    height={120}
                    style={{ objectFit: 'contain', borderRadius: 8 }}
                  />
                  <IconButton
                    color="error"
                    onClick={() => onRemove(file)}
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      bgcolor: 'white',
                      '&:hover': { bgcolor: 'grey.200' },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              );
            })}
          </Stack>
        </Image.PreviewGroup>
      )}

      {/* Video Preview (MUI CardMedia stays as-is) */}
      {accept?.includes('video') && files.length > 0 && (
        <Stack spacing={1}>
          {files.map((file, index) => (
            <Box key={index} sx={{ position: 'relative', maxWidth: 300 }}>
              <video
                src={file instanceof File ? URL.createObjectURL(file) : file}
                controls
                style={{ width: '100%', borderRadius: 8 }}
              />
              <IconButton
                color="error"
                onClick={() => onRemove(file)}
                sx={{
                  position: 'absolute',
                  top: 4,
                  right: 4,
                  bgcolor: 'white',
                  '&:hover': { bgcolor: 'grey.200' },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Stack>
      )}

      {/* Remove All Button */}
      {files.length > 0 && (
        <Button variant="text" color="error" onClick={onRemoveAll}>
          Remove All
        </Button>
      )}

      {maxSize && (
        <Typography variant="caption" color="text.secondary">
          Max file size: {Math.round(maxSize / 1024 / 1024)}MB
        </Typography>
      )}
    </Stack>
  );
};

export default MuiUpload;
